import type { Pluggable } from 'unified';
import { visit } from 'unist-util-visit';
import { z } from 'zod';

// Helper to handle summary directive
function handleSummaryDirective(node: any, parent: any, index: number, attributes: any): void {
  const parsedAttributes = z
    .object({
      title: z.string().optional(),
    })
    .safeParse(attributes);

  parent.children.splice(index, 1, {
    type: 'summary',
    data: {
      hName: 'summary',
      hProperties: parsedAttributes.data,
    },
  });
}

// Helper to handle viewprofile directive
function handleViewProfileDirective(node: any, parent: any, index: number, attributes: any): boolean {
  const parsedAttributes = z
    .object({
      id: z.coerce.number(),
      name: z.string(),
      identifier: z.string(),
      displayValue: z.string().optional(),
    })
    .safeParse(attributes);

  if (!parsedAttributes.success) {
    console.warn(`Invalid viewprofile directive attributes: ${parsedAttributes.error.message}`);
    return true;
  }

  parent.children.splice(index, 1, {
    type: 'viewprofile',
    data: {
      hName: 'viewprofile',
      hProperties: parsedAttributes.data,
    },
  });

  return false;
}

// Helper to handle arrowButton directive
function handleArrowButtonDirective(node: any, parent: any, index: number, attributes: any): boolean {
  const parsedAttributes = z
    .object({
      action: z.string(),
    })
    .safeParse(attributes);

  if (!parsedAttributes.success) {
    console.warn(`Invalid arrowButton directive attributes: ${parsedAttributes.error.message}`);
    return true;
  }

  parent.children.splice(index, 1, {
    type: 'arrowButton',
    data: {
      hName: 'arrowButton',
      hProperties: parsedAttributes.data,
    },
  });

  return false;
}

// Helper to handle queryhint directive
function handleQueryHintDirective(node: any, parent: any, index: number, attributes: any): boolean {
  const parsedAttributes = z
    .object({
      displayValue: z.string(),
      inputValue: z.string(),
      append: z.string().optional(),
    })
    .safeParse(attributes);

  if (!parsedAttributes.success) {
    console.warn(`Invalid queryhint directive attributes: ${parsedAttributes.error.message}`);
    return true;
  }

  parent.children.splice(index, 1, {
    type: 'queryhint',
    data: {
      hName: 'queryhint',
      hProperties: {
        inline: node.type === 'textDirective',
        ...parsedAttributes.data,
      },
    },
  });

  return false;
}

// Helper to handle infoChip directive
// Inline Example: :infoChip{text='Source' tooltipText='Note: Data lag is 3 months'}
// Block Example: ::infoChip{text='Source' tooltipText='Note: Data lag is 3 months'}
function handleInfoChipDirective(node: any, parent: any, index: number, attributes: any): boolean {
  if (node.type === 'containerDirective') {
    console.warn('Container directives are not supported for infoChip');
    return true;
  }

  const parsedAttributes = z
    .object({
      text: z.string(),
      tooltipText: z.string().optional(),
    })
    .safeParse(attributes);

  if (!parsedAttributes.success) {
    console.warn(`Invalid infoChip directive attributes: ${parsedAttributes.error.message}`);
    return true;
  }

  parent.children.splice(index, 1, {
    type: 'infoChip',
    data: {
      hName: 'infoChip',
      hProperties: {
        ...parsedAttributes.data,
        inline: node.type === 'textDirective',
      },
    },
  });

  return false;
}

// Helper to handle badge directive
// Inline Example: :badge[HCP Name = Some Name Here]
// Block Example: ::badge[Content here]{maxWidth='200px'}
function handleBadgeDirective(node: any, parent: any, index: number, attributes: any): boolean {
  if (node.type === 'containerDirective') {
    console.warn('Container directives are not supported for badge directive');
    return true;
  }

  parent.children.splice(index, 1, {
    type: 'badge',
    data: {
      hName: 'badge',
      hProperties: {
        text: node.children.map((child: { value: string }) => child.value).join(''),
        maxWidth: attributes.maxWidth ?? '300px',
        inline: node.type === 'textDirective',
      },
    },
  });

  return false;
}

export const remarkDirectiveToComponent: Pluggable = () => {
  return (tree) => {
    visit(tree, (node, index, parent) => {
      if (
        node.type === 'textDirective' ||
        node.type === 'leafDirective' ||
        node.type === 'containerDirective'
      ) {
        const data = node.data || (node.data = {});
        const attributes = node.attributes || {};

        if (node.name === 'summary') {
          handleSummaryDirective(node, parent, index, attributes);
        } else if (node.name === 'viewprofile') {
          if (handleViewProfileDirective(node, parent, index, attributes)) {
            return;
          }
        } else if (node.name === 'arrowButton') {
          if (handleArrowButtonDirective(node, parent, index, attributes)) {
            return;
          }
        } else if (node.name === 'queryhint') {
          if (handleQueryHintDirective(node, parent, index, attributes)) {
            return;
          }
        } else if (node.name === 'infoChip') {
          if (handleInfoChipDirective(node, parent, index, attributes)) {
            return;
          }
        } else if (node.name === 'badge') {
          if (handleBadgeDirective(node, parent, index, attributes)) {
            return;
          }
        }
      }
    });
  };
};

export const remarkCollapsible: Pluggable = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (
        node.type === 'containerDirective' ||
        node.type === 'leafDirective' ||
        node.type === 'textDirective'
      ) {
        if (node.name !== 'collapsible') return;

        const data = node.data || (node.data = {});
        const { title, open } = node.attributes || {};

        data.hName = 'details';
        data.hProperties = {
          open: open === 'true',
          style: 'margin: 10px 0',
        };

        if (title) {
          node.children.unshift({
            type: 'summary',
            data: {
              hName: 'summary',
              hProperties: {},
            },
            children: [{ type: 'text', value: title }],
          });
        }
      }
    });
  };
};
